import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { type Pagination, toQuery } from "@/api/pagination";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useProductUpdates = (pagination: Pagination) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product-updates", scope.merchantId, scope.workspaceId, pagination.limit, pagination.offset],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["product-updates"].$get({
            param: scope.param,
            query: toQuery(pagination),
          })
        : await client.merchant[":merchantId"]["product-updates"].$get({
            param: scope.param,
            query: toQuery(pagination),
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useActivePopups = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["product-updates", "popup", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["product-updates"].popup.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["product-updates"].popup.$get({
            param: scope.param,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useDismissProductUpdate = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["product-updates"][":id"].dismiss.$post({
            param: { ...scope.param, id },
          })
        : await client.merchant[":merchantId"]["product-updates"][":id"].dismiss.$post({
            param: { ...scope.param, id },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-updates", "popup", scope.merchantId, scope.workspaceId] });
    },
  });
};
